import * as zod from 'zod';

import { HardwareConfigEquipmentMapping } from '@sb/ai/types';
import { ActionRequiredError, Step } from '@sb/remote-control/types';

import { buildConditional } from '../util/conditionalsBuilder';

export namespace RunSkillStep {
  export const name = 'Run learned skill';
  export const description = 'Run an AI learned skill';
  export const librarySection = Step.LibrarySection.Control;
  export const argumentKind = 'RunSkill';
  export const deviceKinds = ['GPUTaskRunner'] as const;

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    taskId: zod.string().nullable().default(null),
    modelPath: zod.string().nullable().default(null),
    timeout: zod.number().default(0),
    secondaryRobotId: zod.string().nullable().default(null),
    gpuTaskRunnerId: zod.string().nullable().default(null),
    isBimanual: zod.boolean().default(false),
    isPrimary: zod.boolean().default(true),
    equipmentKeyToEquipmentId: zod.record(zod.string()).default({}),
    hardwareConfigEquipmentMapping: HardwareConfigEquipmentMapping.default({}),
    condition: zod.string().nullable().default(null),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    if (!args.modelPath) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Model path not specified',
        fieldId: 'modelPath',
      });
    }

    if (args.gpuTaskRunnerId == null) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'No GPU Task runner specified',
        fieldId: 'gpuTaskRunnerId',
      });
    }

    if (args.isBimanual && args.isPrimary && !args.secondaryRobotId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'No secondary control box specified for bimanual inference',
        fieldId: 'secondaryRobotId',
      });
    }

    const condition = args.condition
      ? buildConditional(JSON.parse(args.condition))
      : null;

    return {
      ...stepData,
      stepKind: 'RunSkill',
      args: {
        modelPath: args.modelPath,
        gpuTaskRunnerId: args.gpuTaskRunnerId,
        timeout: args.timeout,
        secondaryRobotId: args.secondaryRobotId,
        isBimanual: args.isBimanual,
        isPrimary: args.isPrimary,
        equipmentKeyToEquipmentId: args.equipmentKeyToEquipmentId,
        hardwareConfigEquipmentMapping: args.hardwareConfigEquipmentMapping,
        condition,
      },
    };
  };

  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration: { args },
  }) => {
    if (args?.argumentKind !== argumentKind) {
      return null;
    }

    if (!args.modelPath) {
      return 'with a model';
    }

    return `with model "${args.modelPath}"`;
  };
}

RunSkillStep satisfies Step.StepKindInfo;
